// Catalog reads/writes — Ghost Laundry.
//
// Read paths hit `services` and `items` (public, RLS read-open). Write
// paths (add/edit/deactivate) are admin-only via `patchService` etc.
// Wiring an item to a service goes through `item_services`.
//
// This module MOVES BYTES ONLY. No pricing math lives here — every total
// runs through src/lib/pricing.js. Anything that looked like a computed
// "Extra lb" line item is gone (spec §0: overage is a consequence of
// weight, not a stored article).

import { supabase } from './supabase'

// ── Reads used by /order ────────────────────────────────────────────────

export async function listActiveServices() {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true, nullsFirst: false })
  if (error) throw error
  return data || []
}

// Load one service row by its id — the pricing table is the truth for
// base_price_cents, included_lbs, extra_lb_cents, monthly_included_lbs,
// handling_fee_pct, so pricing.js can be called with it directly.
export async function getService(serviceId) {
  if (!serviceId) return null
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('id', serviceId)
    .maybeSingle()
  if (error) throw error
  return data
}

// Load one service row by its stable `key` ('members', 'wash_fold', …).
// Use this instead of copying a rate into a component constant: the pricing
// table is the single source, so no screen can quote a price the database
// no longer charges.
export async function getServiceByKey(key) {
  if (!key) return null
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('key', key)
    .maybeSingle()
  if (error) throw error
  return data
}

// All items linked to a given service_id, optionally filtered by `kind`.
//
// Pass `zone` (a pricing_zones.key) to get that zone's catalog: items the
// zone doesn't offer are DROPPED, and items it prices differently come back
// with `price_cents` already overridden. Callers see one list and never
// have to merge two sources — which is how a price ends up wrong.
export async function listItemsForService(serviceId, { kind, zone } = {}) {
  if (!serviceId) return []
  let q = supabase
    .from('items')
    .select('*, item_services!inner(service_id)')
    .eq('item_services.service_id', serviceId)
    .eq('active', true)
    .order('category', { ascending: true, nullsFirst: false })
    .order('sort_order', { ascending: true, nullsFirst: false })
  if (kind) q = q.eq('kind', kind)
  const { data, error } = await q
  if (error) throw error
  const items = data || []
  if (!zone || items.length === 0) return items
  return applyZonePrices(items, zone)
}

/**
 * Overlay a zone's grid on a list of items.
 * No row for an item = the base price, untouched.
 * @param {Array} items
 * @param {string} zoneKey
 */
export async function applyZonePrices(items, zoneKey) {
  if (!zoneKey || !items?.length) return items || []
  const { data: zone } = await supabase
    .from('pricing_zones').select('id').eq('key', zoneKey).maybeSingle()
  if (!zone) return items

  const { data: rows } = await supabase
    .from('item_zone_prices')
    .select('item_id, price_cents, available')
    .eq('zone_id', zone.id)
    .in('item_id', items.map((i) => i.id))

  const byItem = new Map((rows || []).map((r) => [r.item_id, r]))
  return items
    .filter((i) => byItem.get(i.id)?.available !== false)
    .map((i) => {
      const r = byItem.get(i.id)
      return r ? { ...i, price_cents: r.price_cents, zone_price: true } : i
    })
}

/** The zones a customer can land in, for the booking screen. */
export async function listPricingZones() {
  const { data, error } = await supabase
    .from('pricing_zones').select('key, name, dry_cleaning, active').eq('active', true)
  if (error) throw error
  return data || []
}

// How many lbs this member has used in the current CYCLE.
//
// Was `getMemberLbsUsed`, calling the `member_lbs_used` RPC — which counted
// by calendar MONTH and only orders with `service = 'members'`. The two
// functions that actually bill (member_cycle_usage and
// consume_prepaid_lbs_for_order) both count a 28-day cycle and every
// service, so this screen showed a quota the customer was not billed on.
// A member who ordered dry cleaning, or whose cycle straddled the 1st of
// the month, saw the wrong number of lbs left.
//
// Now it calls member_cycle_usage() like everything else. One answer.
//
// Pass `excludeOrderId` when computing the balance for a specific ticket
// (admin ticket flow). Omit when computing a fresh estimate (booking).
export async function getMemberCycleUsedLbs(userId, excludeOrderId = null) {
  if (!userId) return 0
  const { data, error } = await supabase.rpc('member_cycle_usage', {
    p_user: userId,
    p_exclude_order: excludeOrderId,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return Number(row?.cycle_used) || 0
}

// ── Admin catalog writes ───────────────────────────────────────────────

export async function listServicesAll() {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('sort_order', { ascending: true, nullsFirst: false })
  if (error) throw error
  return data || []
}

export async function listItemsAll() {
  const { data, error } = await supabase
    .from('items')
    .select('*, item_services(service_id)')
    .order('category', { ascending: true, nullsFirst: false })
    .order('sort_order', { ascending: true, nullsFirst: false })
  if (error) throw error
  return data || []
}

export async function patchService(id, patch) {
  const { data, error } = await supabase
    .from('services')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function insertService(row = {}) {
  const payload = {
    key: 'new_service',
    name: 'New service',
    pricing_mode: 'per_pound',
    base_price_cents: 0,
    included_lbs: 0,
    extra_lb_cents: 0,
    handling_fee_pct: 12,
    active: false,
    sort_order: 999,
    ...row,
  }
  const { data, error } = await supabase
    .from('services')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function patchItem(id, patch) {
  const { data, error } = await supabase
    .from('items')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

// Hard delete a service. Refuse (return { blocked: true, count }) if any
// orders reference it — the admin has to Active-off instead.
export async function deleteService(id) {
  const { count, error: countErr } = await supabase
    .from('orders')
    .select('*', { count: 'exact', head: true })
    .eq('service_id', id)
  if (countErr) throw countErr
  if ((count || 0) > 0) return { blocked: true, count }
  const { error } = await supabase.from('services').delete().eq('id', id)
  if (error) throw error
  return { blocked: false, count: 0 }
}

// Hard delete an item.
export async function deleteItem(id) {
  const { error } = await supabase.from('items').delete().eq('id', id)
  if (error) throw error
  return { blocked: false }
}

export async function insertItem(row = {}) {
  const payload = {
    name: 'New item',
    price_cents: 0,
    kind: 'garment',
    unit: 'per_item',
    active: false,
    sort_order: 999,
    ...row,
  }
  const { data, error } = await supabase
    .from('items')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  return data
}

// Toggle a link in item_services.
export async function setItemServiceLink(itemId, serviceId, linked) {
  if (linked) {
    const { error } = await supabase
      .from('item_services')
      .upsert({ item_id: itemId, service_id: serviceId }, { onConflict: 'item_id,service_id' })
    if (error) throw error
  } else {
    const { error } = await supabase
      .from('item_services')
      .delete()
      .eq('item_id', itemId)
      .eq('service_id', serviceId)
    if (error) throw error
  }
}

// ── Order items writes ─────────────────────────────────────────────────
//
// Every order_items row references an item from the catalog. Line totals
// are NEVER trusted from the client — pricing.js is authoritative — but
// we still cache `line_total_cents` at write time so the row is self-
// describing (audit, invoice replay, snapshot at that moment).

// Read the current line items attached to an order.
export async function listOrderItems(orderId) {
  if (!orderId) return []
  const { data, error } = await supabase
    .from('order_items')
    .select('*')
    .eq('order_id', orderId)
    .order('id', { ascending: true })
  if (error) throw error
  return data || []
}

// Delete a single line.
export async function deleteOrderItem(itemRowId) {
  const { error } = await supabase.from('order_items').delete().eq('id', itemRowId)
  if (error) throw error
}

// Add one item to an order.
// Rule (spec §9 constraint): if the item is already on the order, we
// INCREMENT its quantity rather than inserting a second row. The DB
// unique constraint (order_id, item_id) enforces this; the client
// upserts to it.
export async function addOrderItem(orderId, item, quantity, { bags = 1, weighedLbs = 0 } = {}) {
  const addQty = Math.max(0, Number(quantity) || 0)
  if (!addQty) return null

  // Look for an existing row for this item on this order.
  const { data: existing, error: findErr } = await supabase
    .from('order_items')
    .select('id, quantity')
    .eq('order_id', orderId)
    .eq('item_id', item.id)
    .maybeSingle()
  if (findErr) throw findErr

  const nextQty = (existing?.quantity || 0) + addQty
  const line_total_cents = computeLineTotal(item, nextQty, { bags, weighedLbs })
  const payload = {
    order_id: orderId,
    item_id: item.id,
    name_snapshot: item.name,
    unit_price_cents: item.price_cents,
    unit: item.unit,
    quantity: nextQty,
    line_total_cents,
  }

  if (existing) {
    const { data, error } = await supabase
      .from('order_items')
      .update(payload)
      .eq('id', existing.id)
      .select()
      .single()
    if (error) throw error
    return data
  }
  const { data, error } = await supabase
    .from('order_items')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  return data
}

// Bulk insert on order creation. Also enforces one row per item —
// duplicate item_ids in `lines` are merged before the insert.
export async function insertOrderItems(orderId, lines) {
  if (!lines || lines.length === 0) return []
  const merged = new Map()
  for (const l of lines) {
    const key = l.item_id
    const prev = merged.get(key)
    if (prev) {
      prev.quantity += Number(l.quantity) || 0
      prev.line_total_cents = computeLineTotal(
        { unit: prev.unit, price_cents: prev.unit_price_cents },
        prev.quantity,
        { bags: l.bags || 1, weighedLbs: l.weighedLbs || 0 },
      )
    } else {
      merged.set(key, {
        order_id: orderId,
        item_id: l.item_id,
        name_snapshot: l.name || l.name_snapshot,
        unit_price_cents: l.price_cents ?? l.unit_price_cents,
        unit: l.unit,
        quantity: Number(l.quantity) || 0,
        line_total_cents: Number(l.line_total_cents) || 0,
      })
    }
  }
  const { data, error } = await supabase
    .from('order_items')
    .insert(Array.from(merged.values()))
    .select()
  if (error) throw error
  return data || []
}

// Line-total snapshot helper — NOT a pricing rule. pricing.js is
// authoritative; this only caches the cents value at write time so an
// invoice can be re-rendered without the catalog.
function computeLineTotal(item, quantity, { bags = 1, weighedLbs = 0 } = {}) {
  const q = Number(quantity) || 0
  const price = Number(item?.price_cents ?? item?.unit_price_cents) || 0
  let mult = 0
  switch (item?.unit) {
    case 'per_item':  mult = q; break
    case 'per_bag':   mult = (Number(bags) || 1) * q; break
    case 'per_order': mult = q > 0 ? 1 : 0; break
    case 'per_lb':    mult = (Number(weighedLbs) || 0) * q; break
    default:          mult = q; break
  }
  return Math.round(price * mult)
}
